// Types
import dayjs from "@/libs/dayjs";
import { LocalClientTime } from "../../lib/office/stores/outlookStore";
export interface ParsedDateTime {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
}

// Constants
export const HOURS_PER_VIEW = 8;
export const TOTAL_VIEWS = 6;
export const HOURS_CHANGE_VIEW = 4;
export const MINUTES_PER_DAY = 24 * 60;

/**
 * Parse ISO string WITHOUT timezone conversion
 * Supports formats: YYYY-MM-DDTHH:MM:SS[±HH:MM] or YYYY-MM-DD
 */
export const parseISOString = (isoString: string | null | undefined): ParsedDateTime => {
  // Handle null, undefined, or empty string
  if (!isoString || typeof isoString !== 'string' || isoString.trim() === '') {
    console.warn("Invalid ISO string (empty or null):", isoString);
    return { year: 0, month: 0, day: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  // Try datetime format first: YYYY-MM-DDTHH:MM:SS (with optional timezone offset)
  // This regex will match the date-time part before any timezone offset
  let match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    return {
      year: parseInt(match[1], 10),
      month: parseInt(match[2], 10),
      day: parseInt(match[3], 10),
      hours: parseInt(match[4], 10),
      minutes: parseInt(match[5], 10),
      seconds: parseInt(match[6], 10),
    };
  }

  // Try date-only format: YYYY-MM-DD
  match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return {
      year: parseInt(match[1], 10),
      month: parseInt(match[2], 10),
      day: parseInt(match[3], 10),
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  console.warn("Invalid ISO string:", isoString);
  return { year: 0, month: 0, day: 0, hours: 0, minutes: 0, seconds: 0 };
};

/**
 * Convert parsed time to minutes since midnight
 */
export const timeToMinutes = (parsed: ParsedDateTime): number => {
  return parsed.hours * 60 + parsed.minutes;
};

/**
 * Check if two parsed dates are on the same day
 */
export const isSameDay = (date1: ParsedDateTime, date2: ParsedDateTime): boolean => {
  return date1.year === date2.year && date1.month === date2.month && date1.day === date2.day;
};

/**
 * Check if two time ranges overlap
 */
export const isOverlap = (start1: number, end1: number, start2: number, end2: number): boolean => {
  return start1 < end2 && start2 < end1;
};

/**
 * Calculate position and width percentage for a time block in the current view
 */
export const calculatePosition = (
  startTime: ParsedDateTime,
  endTime: ParsedDateTime,
  displayDate: ParsedDateTime,
  viewStartHour: number,
  viewEndHour: number
): { left: string; width: string } | null => {
  const startIsSameDay = isSameDay(startTime, displayDate);
  const endIsSameDay = isSameDay(endTime, displayDate);

  // Nếu cả startTime và endTime đều không cùng ngày với displayDate, không vẽ
  if (!startIsSameDay && !endIsSameDay) return null;

  const viewStartMinutes = viewStartHour * 60;
  const viewEndMinutes = viewEndHour * 60;
  const minutesPerDay = 24 * 60; // 1440 minutes

  // Tính toán startMinutes và endMinutes
  let startMinutes: number;
  let endMinutes: number;

  if (startIsSameDay) {
    startMinutes = timeToMinutes(startTime);
  } else {
    // Nếu startTime là ngày hôm trước, coi như bắt đầu từ 0:00
    startMinutes = 0;
  }

  if (endIsSameDay) {
    endMinutes = timeToMinutes(endTime);
  } else {
    // Nếu endTime là ngày hôm sau, coi như kết thúc ở cuối ngày (24:00 = 1440 minutes)
    endMinutes = minutesPerDay;
  }

  // Check if the meeting is in current view
  if (endMinutes <= viewStartMinutes || startMinutes >= viewEndMinutes) {
    return null; // Not in current view
  }

  // Clamp to view boundaries
  const clampedStart = Math.max(startMinutes, viewStartMinutes);
  const clampedEnd = Math.min(endMinutes, viewEndMinutes);
  const viewDuration = viewEndMinutes - viewStartMinutes;

  const left = ((clampedStart - viewStartMinutes) / viewDuration) * 100;
  const width = ((clampedEnd - clampedStart) / viewDuration) * 100;

  return { left: `${left}%`, width: `${width}%` };
};

/**
 * Add days to a ParsedDateTime
 */
export const addDays = (parsed: ParsedDateTime, days: number): ParsedDateTime => {
  const date = new Date(parsed.year, parsed.month - 1, parsed.day);
  date.setDate(date.getDate() + days);
  return {
    ...parsed,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

/**
 * Format ParsedDateTime to YYYY-MM-DD
 */
export const formatDate = (parsed: ParsedDateTime): string => {
  const day = String(parsed.day).padStart(2, "0");
  const month = String(parsed.month).padStart(2, "0");
  const year = parsed.year;
  return `${year}-${month}-${day}`;
};

export const isAllDayMeeting = (startDateTimeZone: ParsedDateTime, endDateTimeZone: ParsedDateTime): boolean => {
  if (
    startDateTimeZone.hours === 0 &&
    startDateTimeZone.minutes === 0 &&
    endDateTimeZone.hours === 0 &&
    endDateTimeZone.minutes === 0 &&
    (startDateTimeZone.year !== endDateTimeZone.year ||
      startDateTimeZone.month !== endDateTimeZone.month ||
      startDateTimeZone.day !== endDateTimeZone.day
    )
  ) {
    const startDate = new Date(
      `${startDateTimeZone.year}-${startDateTimeZone.month.toString().padStart(2, "0")}-${startDateTimeZone.day
        .toString()
        .padStart(2, "0")}T${startDateTimeZone.hours.toString().padStart(2, "0")}:${startDateTimeZone.minutes.toString().padStart(2, "0")}:00`
    ).getTime();
    const endDate = new Date(
      `${endDateTimeZone.year}-${endDateTimeZone.month.toString().padStart(2, "0")}-${endDateTimeZone.day
        .toString()
        .padStart(2, "0")}T${endDateTimeZone.hours.toString().padStart(2, "0")}:${endDateTimeZone.minutes.toString().padStart(2, "0")}:00`
    ).getTime();

    // Tính độ chênh lệch giữa thời gian bắt đầu và kết thúc
    const duration = endDate - startDate;

    // Nếu độ chênh lệch là một ngày đầy đủ, thì sự kiện là cả ngày
    if (duration % 86400000 === 0) {
      // 86400000 milliseconds = 1 day
      return true;
    } else {
      return false;
    }
  }
  return false;
};

/**
 * Calculate which view (0-2) a given hour belongs to
 */
export const getViewFromHour = (hour: number): number => {
  return Math.floor(hour / HOURS_CHANGE_VIEW);
};

/**
 * Generate hour labels for current view with AM/PM only on first and last
 */
export const generateHourLabels = (viewStartHour: number, timeFormat: string): string[] => {
  const labels = [];
  // const hasAMPM = timeFormat.includes("A");
  const hasAMPM = (timeFormat || "").includes("A");

  for (let i = 0; i <= HOURS_PER_VIEW; i++) {
    const hour = viewStartHour + i;
    const isFirst = i === 0;
    const isLast = i === HOURS_PER_VIEW;

    // Create a date object for the current hour (using today's date)
    const date = dayjs().hour(hour).minute(0).second(0).millisecond(0);

    // Format đầy đủ với dayjs trước
    let formatted = date.format(timeFormat);

    // Xử lý chuỗi: loại bỏ phút và khoảng trắng
    if (hasAMPM && (isFirst || isLast)) {
      // Đầu và cuối: loại bỏ phút, giữ lại AM/PM, bỏ khoảng trắng
      formatted = formatted.replace(/:\d{1,2}/g, "");
      formatted = formatted.replace(/\s+/g, "");
    } else {
      // Các label ở giữa: loại bỏ phút và loại bỏ AM/PM
      formatted = formatted.replace(/:\d{1,2}/g, "");
      formatted = formatted.replace(/\s*(AM|PM)/gi, "");
    }

    labels.push(formatted);
  }

  return labels;
};

/**
 * Calculate closed hours blocks (gray bars) for resource opening hours
 * Returns array of position objects for hours outside the opening range
 * @param openStartMinutes Opening start time in minutes since midnight (0-1439)
 * @param openEndMinutes Opening end time in minutes since midnight (0-1439)
 * @param displayDate The date being displayed
 * @param viewStartHour Start hour of current view (0-24)
 * @param viewEndHour End hour of current view (0-24)
 * @returns Array of position objects with left and width percentages, or empty array
 */
export const calculateClosedHoursBlocks = (
  openStartMinutes: number,
  openEndMinutes: number,
  displayDate: ParsedDateTime,
  viewStartHour: number,
  viewEndHour: number
): Array<{ left: string; width: string }> => {
  const blocks: Array<{ left: string; width: string }> = [];
  const viewStartMinutes = viewStartHour * 60;
  const viewEndMinutes = viewEndHour * 60;
  const viewDuration = viewEndMinutes - viewStartMinutes;

  // Before opening: Gray bar from view start to openStartTime (if visible in view)
  if (openStartMinutes > viewStartMinutes) {
    const closedStart = viewStartMinutes;
    const closedEnd = Math.min(openStartMinutes, viewEndMinutes);
    const left = ((closedStart - viewStartMinutes) / viewDuration) * 100;
    const width = ((closedEnd - closedStart) / viewDuration) * 100;

    if (width > 0) {
      blocks.push({ left: `${left}%`, width: `${width}%` });
    }
  }

  // After closing: Gray bar from openEndTime to view end (if visible in view)
  if (openEndMinutes < viewEndMinutes) {
    const closedStart = Math.max(openEndMinutes, viewStartMinutes);
    const closedEnd = viewEndMinutes;
    const left = ((closedStart - viewStartMinutes) / viewDuration) * 100;
    const width = ((closedEnd - closedStart) / viewDuration) * 100;

    if (width > 0) {
      blocks.push({ left: `${left}%`, width: `${width}%` });
    }
  }

  return blocks;
};

/**
 * Convert openTimeOfDayInMinutes to ISO time string for a given date
 * @param minutes Minutes since midnight (0-1439)
 * @param date Date object or ISO string to use for the ISO string
 * @returns ISO time string in format YYYY-MM-DDTHH:MM:00
 */
export const convertMinutesToISOString = (minutes: number, date: Date | string): string => {
  let dateObj: Date;

  // Handle string ISO format
  if (typeof date === "string") {
    if (!date || date.trim() === "") {
      // Fallback to current date if empty string
      dateObj = new Date();
    } else {
      // Parse ISO string to Date object
      dateObj = new Date(date);
      // Validate date
      if (isNaN(dateObj.getTime())) {
        dateObj = new Date();
      }
    }
  } else {
    dateObj = date;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const hourStr = String(hours).padStart(2, "0");
  const minStr = String(mins).padStart(2, "0");
  return `${year}-${month}-${day}T${hourStr}:${minStr}:00`;
};

/**
 * Get opening time as ISO string
 * Handles string ISO format directly
 * @param openTimeString ISO string (e.g., "2025-11-10T07:30:00+01:00") or null/undefined
 * @param displayDate ISO string (e.g., "2025-11-10T07:30:00+01:00"), null, undefined, or empty string (not used for string input, kept for backward compatibility)
 * @returns ISO time string or empty string
 */
export const getOpeningTimeISOString = (
  openTimeString: string | null | undefined,
  displayDate?: string | null | undefined
): string => {
  // If openTimeString is already a string, validate and return it
  if (openTimeString && typeof openTimeString === "string" && openTimeString.trim() !== "") {
    // Validate it's a valid ISO format (contains T and has time part)
    if (openTimeString.includes("T")) {
      return openTimeString;
    }
  }

  return "";
};

/**
 * Compare two ParsedDateTime dates (ignoring time)
 * Returns: -1 if date1 < date2, 0 if date1 === date2, 1 if date1 > date2
 */
export const compareDates = (date1: ParsedDateTime, date2: ParsedDateTime): number => {
  if (date1.year !== date2.year) {
    return date1.year < date2.year ? -1 : 1;
  }
  if (date1.month !== date2.month) {
    return date1.month < date2.month ? -1 : 1;
  }
  if (date1.day !== date2.day) {
    return date1.day < date2.day ? -1 : 1;
  }
  return 0;
};

/**
 * Compare two ParsedDateTime dates with time
 * Returns: -1 if date1 < date2, 0 if date1 === date2, 1 if date1 > date2
 */
export const compareDateTime = (date1: ParsedDateTime, date2: ParsedDateTime): number => {
  const dateCompare = compareDates(date1, date2);
  if (dateCompare !== 0) {
    return dateCompare;
  }
  // Same day, compare time
  const time1 = timeToMinutes(date1);
  const time2 = timeToMinutes(date2);
  if (time1 < time2) return -1;
  if (time1 > time2) return 1;
  return 0;
};

// check if the difference between start and end is more than 1 day
export const isMoreThanOneDayEvent = (startDateTimeZone: string, endDateTimeZone: string): boolean => {
  if (!startDateTimeZone || !endDateTimeZone) {
    return false;
  }

  const parsedStart = parseISOString(startDateTimeZone);
  const parsedEnd = parseISOString(endDateTimeZone);
  const startDate = new Date(
    parsedStart.year,
    parsedStart.month - 1,
    parsedStart.day,
    parsedStart.hours,
    parsedStart.minutes,
    parsedStart.seconds
  );
  const endDate = new Date(
    parsedEnd.year,
    parsedEnd.month - 1,
    parsedEnd.day,
    parsedEnd.hours,
    parsedEnd.minutes,
    parsedEnd.seconds
  );
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return diffDays > 1;
}

/**
 * Extract date part (YYYY-MM-DD) from ISO string
 */
const extractDatePart = (isoString: string): string => {
  // Match YYYY-MM-DD pattern (before T or end of string)
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

/**
 * Extract time part (HH:MM:SS) from ISO string and convert to minutes since midnight
 */
const extractTimeMinutes = (isoString: string): number => {
  // Match HH:MM:SS or HH:MM pattern
  const timeMatch = isoString.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    return hours * 60 + minutes;
  }
  return 0; // Default to midnight if no time found
};

/**
 * Compare two date strings (YYYY-MM-DD format)
 * Returns: -1 if date1 < date2, 0 if date1 === date2, 1 if date1 > date2
 */
const compareDateStrings = (date1: string, date2: string): number => {
  if (date1 < date2) return -1;
  if (date1 > date2) return 1;
  return 0;
};

/**
 * Calculate date range blocks (gray bars) for resource opening/closing dates
 * Logic:
 * - closingDate: Resource closes FROM this date/time (e.g., 2025-11-17T06:00:00 = closes from 6h on Nov 17)
 * - openingDate: Resource opens FROM this date/time (e.g., 2025-11-19T06:00:00 = opens from 6h on Nov 19)
 * - Between closingDate and openingDate: Resource is CLOSED (gray bars)
 *
 * Uses string comparison directly, no Date object conversion
 *
 * @param openingDate Opening date string (ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS) or null/undefined
 * @param closingDate Closing date string (ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS) or null/undefined
 * @param displayDate The date being displayed (ParsedDateTime)
 * @param viewStartHour Start hour of current view (0-24)
 * @param viewEndHour End hour of current view (0-24)
 * @returns Array of position objects with left and width percentages, or empty array
 */
export const calculateDateRangeBlocks = (
  openingDate: string | null | undefined,
  closingDate: string | null | undefined,
  displayDate: ParsedDateTime,
  viewStartHour: number,
  viewEndHour: number
): Array<{ left: string; width: string }> => {
  const blocks: Array<{ left: string; width: string }> = [];

  // If no opening/closing dates provided, don't show any blocks
  if (!openingDate && !closingDate) {
    return blocks;
  }

  // Convert displayDate to date string (YYYY-MM-DD)
  const displayDateStr = `${displayDate.year.toString().padStart(4, "0")}-${displayDate.month
    .toString()
    .padStart(2, "0")}-${displayDate.day.toString().padStart(2, "0")}`;

  // Extract date parts from ISO strings
  const closingDateStr = closingDate ? extractDatePart(closingDate) : "";
  const openingDateStr = openingDate ? extractDatePart(openingDate) : "";

  const viewStartMinutes = viewStartHour * 60;
  const viewEndMinutes = viewEndHour * 60;
  const viewDuration = viewEndMinutes - viewStartMinutes;

  // Case 1: displayDate is before closingDate (before closing) → no gray bars
  if (closingDateStr && compareDateStrings(displayDateStr, closingDateStr) < 0) {
    return blocks; // Resource is still open
  }

  // Case 2: displayDate is after openingDate (after opening) → no gray bars
  if (openingDateStr && compareDateStrings(displayDateStr, openingDateStr) > 0) {
    return blocks; // Resource is open again
  }

  // Case 3: displayDate is between closingDate and openingDate → CLOSED
  // Check if displayDate is the same day as closingDate or openingDate
  const isSameDayAsClosing = closingDateStr && displayDateStr === closingDateStr;
  const isSameDayAsOpening = openingDateStr && displayDateStr === openingDateStr;

  if (isSameDayAsClosing && isSameDayAsOpening) {
    // Same day for both closing and opening (shouldn't happen, but handle it)
    // Gray from closing time to opening time
    const closingMinutes = closingDate ? extractTimeMinutes(closingDate) : 0;
    const openingMinutes = openingDate ? extractTimeMinutes(openingDate) : 0;

    if (closingMinutes < openingMinutes) {
      // Gray bar from closing time to opening time
      const closedStart = Math.max(closingMinutes, viewStartMinutes);
      const closedEnd = Math.min(openingMinutes, viewEndMinutes);

      if (closedEnd > closedStart) {
        const left = ((closedStart - viewStartMinutes) / viewDuration) * 100;
        const width = ((closedEnd - closedStart) / viewDuration) * 100;
        blocks.push({ left: `${left}%`, width: `${width}%` });
      }
    }
  } else if (isSameDayAsClosing) {
    // Same day as closingDate → gray from closing time to end of day
    const closingMinutes = closingDate ? extractTimeMinutes(closingDate) : 0;
    const closedStart = Math.max(closingMinutes, viewStartMinutes);
    const closedEnd = viewEndMinutes;

    if (closedEnd > closedStart) {
      const left = ((closedStart - viewStartMinutes) / viewDuration) * 100;
      const width = ((closedEnd - closedStart) / viewDuration) * 100;
      blocks.push({ left: `${left}%`, width: `${width}%` });
    }
  } else if (isSameDayAsOpening) {
    // Same day as openingDate → gray from start of day to opening time
    const openingMinutes = openingDate ? extractTimeMinutes(openingDate) : 0;
    const closedStart = viewStartMinutes;
    const closedEnd = Math.min(openingMinutes, viewEndMinutes);

    if (closedEnd > closedStart) {
      const left = ((closedStart - viewStartMinutes) / viewDuration) * 100;
      const width = ((closedEnd - closedStart) / viewDuration) * 100;
      blocks.push({ left: `${left}%`, width: `${width}%` });
    }
  } else {
    // displayDate is between closingDate and openingDate (different days) → full day gray
    blocks.push({ left: "0%", width: "100%" });
  }

  return blocks;
};

export function dateTimeToTicks(date: any): number {
  return date * 10000 + 621355968000000000;
}

export function convertTicksToDateTime(ticks: number): string {
  return new Date((ticks - 621355968000000000)/1000).toISOString();
}

export function convertLocalTimeToString(time: LocalClientTime | null): string {
  if (!time) return "";
  return `${time.year}-${((time.month ?? 0) + 1).toString().padStart(2, "0")}-${(time as LocalClientTime).date
    .toString()
    .padStart(2, "0")}T${time.hours.toString().padStart(2, "0")}:${time.minutes.toString().padStart(2, "0")}`;
}

export function convertParsedTimeToString(time: ParsedDateTime | null): string {
  if (!time) return "";
  return `${time.year}-${time.month.toString().padStart(2, "0")}-${time.day.toString().padStart(2, "0")}T${time.hours
    .toString()
    .padStart(2, "0")}:${time.minutes.toString().padStart(2, "0")}`;
}

export function handleSortDate(dates: string[]): string[] {
  return dates.sort((a, b) => compareDateStrings(a, b));
}